/**
 * Episode entity class
 */

import moment from 'moment'
import { timeStringToDate, formatDateByTimezone } from 'app/helpers/date'

/**
 * EpisodeFactory
 * returns Episode entity class
 *
 * @class EpisodeFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Episode} class
 *
 * @ngdoc factory
 * @name mwApp.factory:EpisodeFactory
 */
/* @ngInject */
export default function EpisodeFactory ($http, Entity, Type, FileUploadMixin, ShowSet, Network, UrlPolicy, alertLiteSvc, authSvc) {
  /* === Episode class === */

  /**
   * Episode model
   *
   * @class Episode
   * @classdesc Episode model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @return {models.Episode} Episode entity
   */
  function Episode () {
    var obj = Entity.apply(this, arguments)
    /* istanbul ignore if: covered by Entity cache integration; unit test would require shared EntityCache scoping */
    if (obj) {
      return obj
    }
  }
  Episode.prototype = Object.create(Entity.prototype)
  Episode.prototype.constructor = Episode

  /* === Episode instance methods & fields === */

  Object.assign(Episode.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Episode#
     * @type {Object}
     */
    _fields: {
      id: Number,
      title: String,
      description: String,
      directors: String,
      stars: String,
      releasedTime: String,
      resolution: 'HD',
      ageRatings: String,
      length: String,
      enabled: Boolean,
      sortOrder: Number,
      displayNumber: String,
      views: Number,
      portraitImageUrl: String,
      backgroundImageUrl: String,
      season: {
        name: String,
        newEpisodePosition: String
      },
      urls: {
        STB: 'MediaUrlDTO',
        PC: 'MediaUrlDTO',
        iOS: 'MediaUrlDTO',
        Android: 'MediaUrlDTO',
        SamsungTV: 'MediaUrlDTO',
        Others: 'MediaUrlDTO',
        WEB: 'MediaUrlDTO'
      },
      tvShow: { // TODO: use $-notation
        id: Number
      },
      publishTime: String,
      unpublishTime: String,
      contentSets: ['ShowSet'],
      assignedSubscriptions: ['Subscription'],
      defaultStream: 'DefaultStream',
      policedStreams: null, // 'PolicedStreams'
      streamPolicy: 'UrlPolicy',
      streamBindings: ['StreamBinding'],
      dolbyAtmosAvailable: Boolean,

      /* local variables */
      $networksSets: [{
        network: null,
        showSet: null,
        sets: []
      }],
      $scheduledPublishing: Boolean,
      $fromDate: Date,
      $fromTime: Date,
      $toDate: Date,
      $toTime: Date,
      $toDateUnlimited: true,
      $itemType: 'episode',
      $itemTypeForContentSet: 'show',
      $contentSetType: 'showSet',
      $contentSetName: 'Show Set',
      $tvShow: 'Show',
      $justCreated: Boolean
    },

    /**
     * url template
     *
     * @memberof models.Episode#
     * @type {String}
     */
    _urlTemplate: 'api/tvshows/{{::tvShow.id}}/episodes/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Episode#
     * @type {String}
     */
    _publicUrlTemplate: 'shows/{{::tvShow.id}}/seasons/none/episodes/{{::id}}',

    /**
     * Prepare object
     *
     * @memberof models.Episode#
     * @override
     */
    _prepare: function () {
      UrlPolicy.prepareToLoadContentItem(this)

      if ((!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED')) && this.urls) {
        delete this.urls.WEB
      }

      /* Note: set default values. */
      this.$scheduledPublishing = !!this.publishTime
      this.$toDateUnlimited = !this.unpublishTime

      this.releasedTime = this.releasedTime ? moment(this.releasedTime).toDate() : moment().toDate()
      this.$fromDate = this.publishTime ? formatDateByTimezone(this.publishTime, false).toDate() : moment().toDate()
      this.$toDate = this.unpublishTime ? formatDateByTimezone(this.unpublishTime, false).toDate() : this.$fromDate
      this.$fromTime = timeStringToDate(this.publishTime ? moment.utc(this.publishTime).format('HH:mm') : null, 'startOfDay')
      this.$toTime = timeStringToDate(this.unpublishTime ? moment.utc(this.unpublishTime).format('HH:mm') : null, 'endOfDay')
    },

    /**
     * Get underlying object
     *
     * @memberof models.ReportSubscribersSeed#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      UrlPolicy.prepareToSaveContentItem(this)

      this.releasedTime = moment(this.releasedTime).startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]')

      const fromDate = this.$fromDate.setHours(this.$fromTime.getHours(), this.$fromTime.getMinutes(), 0)
      const toDate = this.$toDate.setHours(this.$toTime.getHours(), this.$toTime.getMinutes(), 0)
      this.publishTime = formatDateByTimezone(fromDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      this.unpublishTime = formatDateByTimezone(toDate).format('YYYY-MM-DDTHH:mm:ss[Z]')

      if (this.$toDateUnlimited) {
        this.unpublishTime = null
      }

      if (!this.$scheduledPublishing) {
        this.publishTime = null
        this.unpublishTime = null
      }

      const ret = Entity.prototype._object.apply(this, arguments)

      // delete null urls
      if (ret.urls) {
        ret.urls =
          Object.keys(ret.urls)
            .reduce(
              (o, platform) => {
                if (ret.urls[platform] != null) {
                  o[platform] = ret.urls[platform]
                }
                return o
              },
              Object.create(null)
            )
      }

      return ret
    },

    /**
     * Patch method
     *
     * @memberof models.Episode#
     * @param {String} url
     * @param params
     * @return {Promise} promise
     * @private
     */
    _patch: function (url, params) {
      return $http.patch(url, params)
        .then(
          response => {
            if (response.data) {
              this._weakCopy(response.data)
            }
            return response
          }
        ).catch(
          rejection => {
            return alertLiteSvc.alertError(rejection)
          })
    },

    /**
     * On/Off Entity, performing PATCH request to the server
     *
     * @memberof models.Episode#
     * @param {Boolean} [enabled=this.enabled] - enable or disable Entity
     * @return {Promise} promise
     */
    _onOff: function (enabled) {
      if (typeof enabled !== 'boolean') {
        enabled = this.enabled
      }

      return this._patch(this._url + '/status', enabled ? '"ENABLE"' : '"DISABLE"')
        .catch(rejection => {
          this.enabled = !this.enabled
          return rejection
        })
    },

    /**
     * Change order Episode, performing PATCH request to the server
     *
     * @memberof models.Episode#
     * @param {Boolean} [sortOrder=this.sortOrder] - enable or disable Entity
     * @param {Number} [seasonId]
     * @return {Promise} promise
     */
    _order: function (sortOrder, seasonId) {
      return this._patch(this._url + '/order', { sortOrder, seasonId })
        .catch(
          rejection => {
            return alertLiteSvc.alertError(rejection)
          }
        )
    },

    /**
     * Load entities for SmartTable <br>
     * SmartTable state looks like Entity.loadSmart
     *
     * @memberof models.Episode
     * @param {Object} tableState - SmartTable state
     * @return {Promise} promise
     */
    _updateSmart: function (tableState) {
      return Episode.loadSmart(tableState, { tvShow: { id: this.tvShow.id } })
    },

    /**
     * Update entity, performing GET request
     *
     * @memberof models.Episode#
     * @return {Promise} promise
     */
    _updateCustom: function (id) {
      // const { id } = this.tvShow // save tv show id

      return this._update()
        .then(() => {
          this.tvShow = { id }
          return this
        })
    },
    /**
     * Get image URL for given size
     *
     * @memberof models.Season#
     * @param  {String} fieldName
     * @param  {String} size
     * @return {String}
     */
    _urlFor: function (fieldName, size) {
      return this[fieldName]
        ? this[fieldName].indexOf('{{size}}') !== -1
          ? this[fieldName].replace('{{size}}', size) + '?lastUpload=' + this.$lastUpload
          : this[fieldName] + '?resize=' + size + '&lastUpload=' + this.$lastUpload
        : ''
    }

  }, FileUploadMixin)

  /* === Episode static methods & fields === */

  /**
   * Load Content sets
   *
   * @returns {*}
   * @param show
   * @param id
   */
  Episode.loadAvailableContentSets = function (show, id) {
    return $http.get(`/nora/api/tvshows/${show.id}/episodes/${id}/extra/content-sets`)
      .data()
      .then(data => data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  // copy all static methods from Entity class
  Object.assign(Episode, Entity)
  Episode._name = 'Episode'
  Episode.init()

  // permissions
  Episode.access = {
    view: 'viewVodSeries',
    create: 'createVodSeries',
    edit: 'editVodSeries',
    delete: 'deleteVodSeries'
  }

  // extra
  Episode.extra = {
    drmProfiles: ['DRMProfile']
  }

  return Episode
}
